import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
    filterBalances,
    handleCryptoAmountValidation,
    removeCommas,
    toFixedRound,
    useJunoCommonFunctions,
    validateAmount,
    validateInputAmount,
} from '../../../../client/Juno/helpers';
import { apiService } from '../../../../client/common/apiCallService';
import TransactionStatus from '../../TransactionStatus';
import SellCurrencyForm from './SellCurrencyForm';
import SellModal from './SellModal';
import JunoButton from '../../common/JunoButton';

export const SellCryptoTransaction = ({
    open: propOpen,
    onClose: onCloseModal,
    row: selectedCurrency,
}) => {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        propOpen ? setOpen(true) : setOpen(false);
    }, [propOpen]);

    const apiUrl = useSelector(state => state.config.api_url);
    const clientData = useSelector(state => state.juno?.clientData);
    const {
        handleCryptoExchangeRate,
        getCryptoBalances,
        getCryptoFee,
        getTransactions,
        getBalances,
    } = useJunoCommonFunctions();

    const { balancesCurrencies, balancesCrypto, unfreezeCryptoCurrencies } = useSelector(
        state => state.juno,
    );
    const updatedUnfreezeCryptoCurrencies = filterBalances(unfreezeCryptoCurrencies);
    const [isConfirmModal, setIsConfirmModal] = useState(false);
    const [amountError, setAmountError] = useState('');
    const [exchangeRate, setExchangeRate] = useState(null);
    const [fee, setFee] = useState(null); // Default fee should be  null
    const [transactionFee, setTransactionFee] = useState(0);
    const [successModal, setSuccessModal] = useState(false);
    const [failedModal, setFailedModal] = useState({ open: false, msg: '' });
    const [quotationData, setQuotationData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [exchangeLoading, setExchangeLoading] = useState(false);
    const [pendingModal, setPendingModal] = useState({ open: false, msg: '' });
    const [selectedCryptoCurrency, setSelectedCryptoCurrency] = useState(selectedCurrency);
    const [selectedFiatCurrency, setSelectedFiatCurrency] = useState(null);
    const [continueBtn, setContinueBtn] = useState(false);
    const modalTimerRef = useRef(null);
    const [quotationError, setQuotationError] = useState('');

    const [formData, setFormData] = useState({
        cryptoAmount: '',
        fiatAmount: '',
        cryptoId: '',
        fiatCurrency: '',
    });
    const [lastEnteredInput, setLastEnteredInput] = useState('');

    useEffect(() => {
        if (updatedUnfreezeCryptoCurrencies && balancesCurrencies && !successModal) {
            setFormData(prevFormData => ({
                ...prevFormData,
                cryptoId: selectedCryptoCurrency
                    ? selectedCryptoCurrency?.currencyShortName
                    : updatedUnfreezeCryptoCurrencies[0]?.currencyShortName,
                fiatCurrency: balancesCurrencies[0]?.currencyShortName,
                cryptoAmount: '',
                fiatAmount: '',
            }));
        }
    }, [unfreezeCryptoCurrencies, balancesCurrencies, successModal]);

    const handleClose = (resetForm = true) => {
        setOpen(false);
        setIsLoading(false);
        setIsConfirmModal(false);
        setAmountError('');
        if (resetForm) resetFormData();
        setQuotationData({});
        setSelectedFiatCurrency(balancesCurrencies[0]);
        setSelectedCryptoCurrency(updatedUnfreezeCryptoCurrencies[0]);
        clearTimeout(modalTimerRef.current);
        setContinueBtn(false);
    };

    const handleAmountChange = e => {
        if (!exchangeRate) return;

        // Prevent calculations if fee is null
        if (fee === null) {
            setAmountError('Unable to fetch transaction fee. Please try again.');
            return;
        }

        const name = e.target.name;
        const sanitizedValue = removeCommas(validateInputAmount(e.target.value));
        const numValue = parseFloat(sanitizedValue);

        let newCryptoAmount;
        let newFiatAmount;
        let newTransactionFee = 0;
        let amountError = '';
        let fiatAmountError = '';

        if (name === 'cryptoAmount') {
            setLastEnteredInput('crypto');
            newCryptoAmount = sanitizedValue;
            if (isNaN(numValue)) {
                newFiatAmount = '';
            } else {
                newTransactionFee = +(numValue * fee).toFixed(8);
                const cryptoAmountAfterFee = numValue - newTransactionFee;
                newFiatAmount = cryptoAmountAfterFee * exchangeRate;
                newFiatAmount = toFixedRound(newFiatAmount, 2);
            }
        } else if (name === 'fiatAmount') {
            setLastEnteredInput('fiat');
            newFiatAmount = sanitizedValue;
            if (isNaN(numValue)) {
                newCryptoAmount = '';
            } else {
                const cryptoForFiat = numValue / exchangeRate;
                newTransactionFee = +(cryptoForFiat * fee).toFixed(8);
                newCryptoAmount = cryptoForFiat + newTransactionFee;
                newCryptoAmount = toFixedRound(newCryptoAmount, 8);
            }
        }

        const cryptoValidationError = handleCryptoAmountValidation(
            removeCommas(newCryptoAmount),
            formData.cryptoId,
            balancesCrypto,
        );
        const cryptoAmountError = validateAmount(
            removeCommas(newCryptoAmount),
            0,
            formData.cryptoId,
            balancesCrypto,
        );
        if (parseFloat(newFiatAmount) <= 0 && !isNaN(parseFloat(newFiatAmount))) {
            fiatAmountError = '* Amount must be greater than 0';
        }
        amountError = cryptoValidationError || cryptoAmountError || fiatAmountError;

        setFormData({ ...formData, cryptoAmount: newCryptoAmount, fiatAmount: newFiatAmount });
        setTransactionFee(newTransactionFee);
        setAmountError(amountError);
        setQuotationError('');
    };

    const createCryptoTransaction = async () => {
        setIsLoading(true);
        clearTimeout(modalTimerRef.current);
        setContinueBtn(true);

        const blockchain = balancesCrypto.find(
            crypto => crypto.currencyShortName === formData.cryptoId,
        )?.blockchain;

        const payload = {
            currencyType: 'crypto',
            type: 'Sell',
            transactionEmail: localStorage.getItem('user_name'),
            transactionFee: transactionFee,
            transactionDetails: {
                quotationId1: quotationData?.quotationId1,
                quotationId2: quotationData?.quotationId2,
                cryptoAmount: removeCommas(formData.cryptoAmount),
                fiatAmount: removeCommas(formData.fiatAmount),
                cryptoId: formData.cryptoId,
                blockchain,
                fiatCurrency: formData.fiatCurrency,
                quotationPrice: +parseFloat(quotationData?.price),
                fee: fee * 100,
                calculationMethod: lastEnteredInput,
                netCryptoAmount: Number(
                    (parseFloat(formData.cryptoAmount) - transactionFee).toFixed(8),
                ),
                exchangeRate: +parseFloat(exchangeRate).toFixed(8),
            },
        };

        const result = await handleApiRequest({
            url: '/restapi/create-client-transaction-crypto',
            payload,
        });

        if (result?.data?.status === 'success') {
            getCryptoBalances();
            getBalances();
            setSuccessModal(true);
            handleClose(false);
            getTransactions();
        } else if (result?.data?.status === 'pending') {
            setPendingModal({ open: true, msg: result?.data?.msg });
            handleClose(false);
        } else {
            setIsLoading(false);
            const errorMsg = result.error?.toString() || '';
            setFailedModal({ open: true, msg: errorMsg });
            handleClose(false);
        }
    };

    const handleExchangeRate = async () => {
        setExchangeLoading(true);
        const payload = {
            cryptoList: [formData.cryptoId],
            fiatAmount: 1,
            fiatCurrency: formData.fiatCurrency,
            exchangeConversionType: 'fiat-to-crypto',
        };
        try {
            const response = await handleCryptoExchangeRate(payload);
            if (response?.cryptoAmounts[formData?.cryptoId]) {
                setExchangeRate(+(1 / response?.cryptoAmounts[formData.cryptoId]).toFixed(2));
                setAmountError('');
            } else {
                setExchangeRate(null);
                setAmountError('Failed to fetch exchange rates. Please try again.');
            }
        } catch {
            setExchangeRate(null);
            setAmountError('Failed to fetch exchange rates. Please try again.');
        }
        setExchangeLoading(false);
    };

    const fetchRateAndFee = async () => {       
        if (!formData.cryptoId) return;

        if (formData.fiatCurrency) {
            await handleExchangeRate();
        }
        const payload = {
            crypto: formData.cryptoId,
            profile: clientData.feeProfile,
            transactionType: 'Sell',
        };
        try {
            const cryptoFee = await getCryptoFee(payload);
            // Keep fee as null if API fails or returns error
            const feeValue = !cryptoFee?.error ? Number(cryptoFee?.data?.fee) : NaN;
            setFee(!isNaN(feeValue) ? feeValue / 100 : null);
        } catch (error) {
            console.error('Failed to fetch crypto fee:', error);
            setFee(null); // Set to null on error
        }
    };

    useEffect(() => {
        fetchRateAndFee();
    }, [formData.cryptoId, formData.fiatCurrency, open]);

    const handleAmountAndSetFormData = e => {
        setFormData({
            ...formData,
            cryptoId: e,
            cryptoAmount: null,
            fiatAmount: null,
        });
        setAmountError('');
        setTransactionFee(0);
    };

    const handleApiRequest = async ({ url, payload }) => {
        try {
            const fullUrl = (window.location.hostname === 'localhost' ? apiUrl : '') + url;

            const response = await new Promise((resolve, reject) => {
                apiService(
                    fullUrl,
                    payload,
                    res => {
                        if (!res.error) resolve(res);
                        else reject(res);
                    },
                    err => reject(err),
                );
            });

            return { success: true, data: response };
        } catch (error) {
            return { success: false, error };
        }
    };

    const handleQuotationAmount = async () => {
        // Prevent quotation if fee is null
        if (fee === null) {
            setQuotationError('Unable to fetch transaction fee. Please try again.');
            return;
        }

        const blockchain = balancesCrypto.find(
            crypto => crypto.currencyShortName === formData.cryptoId,
        )?.blockchain;

        const cryptoAmountForTrade = parseFloat(formData.cryptoAmount) - transactionFee;
        const isCryptoInput = lastEnteredInput === 'crypto';
        const isFiatInput = lastEnteredInput === 'fiat';

        const payload = {
            userId: clientData?.accountNumber,
            crypto: formData.cryptoId,
            blockchain: blockchain,
            ...(isCryptoInput && {
                cryptoAmount: Number(removeCommas(cryptoAmountForTrade)).toFixed(6),
            }),
            ...(isFiatInput && {
                fiatAmount: Number(removeCommas(formData.fiatAmount)).toFixed(2),
            }),
            side: 'Sell',
            fiat: formData.fiatCurrency,
        };

        const result = await handleApiRequest({
            url: '/restapi/request-for-quotation',
            payload,
        });

        if (result.success && result?.data?.data?.quotationId1) {
            const resData = result.data?.data;
            setQuotationData(resData);
            if (lastEnteredInput === 'crypto') {
                setFormData({ ...formData, fiatAmount: +resData?.receiveQuantity });
            } else if (lastEnteredInput === 'fiat') {
                const cryptoForFiat = +resData?.deliverQuantity;
                const newTransactionFee = +(cryptoForFiat * fee).toFixed(8);
                const newCryptoAmount = cryptoForFiat + newTransactionFee;
                setFormData({ ...formData, cryptoAmount: toFixedRound(newCryptoAmount, 8) });
                setTransactionFee(newTransactionFee);
            }
        } else {
            console.log(result.error);
            setQuotationData({ error: true });
        }
    };

    const handleSuccessModal = () => {
        setSuccessModal(false);
        resetFormData();
        fetchRateAndFee(true);
    };

    const resetFormData = () => {
        setFormData({
            cryptoAmount: '',
            fiatAmount: '',
            cryptoId: updatedUnfreezeCryptoCurrencies[0]?.currencyShortName || null,
            fiatCurrency: balancesCurrencies[0]?.currencyShortName || null,
        });
        setFee(null)
    };

    const handleFailedModal = () => {
        setFailedModal({ open: false, msg: '' });
        resetFormData();
        setIsConfirmModal(false);
        handleClose();
        fetchRateAndFee(true);
    };

    const handleProcessModal = () => {
        setPendingModal({ open: false, msg: '' });
        setFormData({
            cryptoAmount: null,
            fiatAmount: null,
            cryptoId: 'ETH',
            fiatCurrency: 'USD',
        });
        setFee(null)
        setIsConfirmModal(false);
        getCryptoBalances();
        getBalances();
        getTransactions();
    };

    const handleConfirmationModal = () => {
        if (formData.fiatAmount && formData.cryptoAmount && !amountError && fee !== null) {
            setIsConfirmModal(true);
            handleQuotationAmount();
        }
        modalTimerRef.current = setTimeout(() => {
            setQuotationError('Quote expired, please re-enter the amounts.');
            setIsConfirmModal(false);
            fetchRateAndFee();

            setTimeout(() => {
                setQuotationError('');
            }, 10000);
        }, 30000);
    };

    useEffect(() => {
        let intervalId = null;
        if (isConfirmModal && !continueBtn) {
            intervalId = setInterval(() => {
                handleQuotationAmount();
            }, 5000);
        } else {
            clearInterval(intervalId);
        }
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [isConfirmModal, continueBtn]);

    const currencyProps = useMemo(
        () => ({
            formData,
            amountError,
            handleAmountChange,
            handleAmountAndSetFormData,
            setFormData,
            selectedCryptoCurrency,
            setSelectedCryptoCurrency,
            selectedFiatCurrency,
            setSelectedFiatCurrency,
            exchangeLoading,
            exchangeRate,
            quotationError,
        }),
        [
            formData,
            amountError,
            handleAmountChange,
            handleAmountAndSetFormData,
            setFormData,
            selectedCryptoCurrency,
            setSelectedCryptoCurrency,
            selectedFiatCurrency,
            setSelectedFiatCurrency,
            exchangeLoading,
            exchangeRate,
            quotationError,
        ],
    );

    useEffect(() => {
        setSelectedFiatCurrency(balancesCurrencies[0]);
    }, [balancesCurrencies]);

    return (
        <div>
            {!propOpen && (
                <div className="flex flex-col  items-center !gap-2.5">
                    <div className="bg-modal-bg p-3 w-full rounded-lg h-[444px] border border-[#E4E4E7] dark:!border-0 bg-quickforms-bg">
                        <SellCurrencyForm {...currencyProps} />
                    </div>
                    <div className="w-full">
                        <JunoButton
                            type="widget"
                            onClick={() => {
                                setOpen(true);
                                handleConfirmationModal();
                            }}
                            disabled={
                                !formData.cryptoAmount ||
                                !formData.fiatAmount ||
                                amountError ||
                                !exchangeRate ||
                                fee === null // Added fee null check
                            }
                            dataE2e="continue-button">
                            Continue
                        </JunoButton>
                    </div>
                </div>
            )}
            <SellModal
                {...currencyProps}
                quotationData={quotationData}
                onClickCall={createCryptoTransaction}
                isLoading={isLoading}
                isConfirmModal={isConfirmModal}
                setIsConfirmModal={setIsConfirmModal}
                isOpen={open}
                handleConfirmationModal={() => handleConfirmationModal()}
                onClose={handleClose}
                handleModalAction={onCloseModal}
                setQuotationData={setQuotationData}
                modalTimerRef={modalTimerRef}
            />

            {successModal && (
                <TransactionStatus
                    status="success"
                    currency={formData.fiatCurrency}
                    amount={formData.fiatAmount}
                    transactionType={'SELL'}
                    description="Has been deposited into your Juno Money wallet."
                    onClick={handleSuccessModal}
                    buttonText="CLOSE"
                />
            )}
            {pendingModal.open && (
                <TransactionStatus
                    status="pending"
                    currency={formData.fiatCurrency}
                    amount={formData.fiatAmount}
                    description="Sell pending, check your transaction history to track it's progress"
                    onClick={handleProcessModal}
                    buttonText="Track Transaction"
                />
            )}
            {/* Failed/Error modal */}
            {failedModal.open && (
                <TransactionStatus
                    status="error"
                    currency={formData.fiatCurrency}
                    amount={formData.fiatAmount}
                    description={failedModal.msg || ''}
                    transactionType={'SELL'}
                    onClick={handleFailedModal}
                    buttonText="CLOSE"
                />
            )}
        </div>
    );
};